import { createContext, useCallback, useContext, useEffect, useState } from "react";
import * as api from "../api/client";

const ConversationsContext = createContext(null);

const ACTIVE_KEY = "fv-active";

export function ConversationsProvider({ children }) {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem(ACTIVE_KEY) || null);
  // Chat ids checked in the sidebar for building a dashboard. Separate from `activeId` (the open chat) —
  // you can be reading one conversation while selecting a different set to combine into a dashboard.
  const [selected, setSelected] = useState(() => new Set());

  const refresh = useCallback(async () => {
    try {
      setConversations(await api.listConversations());
    } catch {
      // sidebar refresh is best-effort — a transient failure shouldn't disrupt an active chat
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setActiveId = useCallback((id) => {
    setActiveIdState(id);
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  }, []);

  const toggleSelected = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelected = useCallback(() => setSelected(new Set()), []);

  const removeConversation = useCallback(
    (id) => {
      setConversations((prev) => prev.filter((c) => c.id !== id));
      setSelected((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    []
  );

  // Optimistic: called after the rename API call succeeds, to reflect the new title in the sidebar
  // and the chat header without waiting on a full `refresh()` round-trip.
  const renameConversationLocal = useCallback((id, title) => {
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
  }, []);

  const value = {
    conversations,
    refresh,
    activeId,
    setActiveId,
    selected,
    toggleSelected,
    clearSelected,
    removeConversation,
    renameConversationLocal,
  };

  return <ConversationsContext.Provider value={value}>{children}</ConversationsContext.Provider>;
}

export function useConversations() {
  const ctx = useContext(ConversationsContext);
  if (!ctx) throw new Error("useConversations must be used within a ConversationsProvider");
  return ctx;
}
